import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, signal, WritableSignal } from '@angular/core';
import { form, required } from '@angular/forms/signals';
import { MatIconModule } from '@angular/material/icon';
import { PropertyCountry, PropertyGeolocationType, PropertyLocationDto } from '@workspace/data-types';
import { AbstractSignalFormComponent, OpenStreetMapComponent } from '@workspace/ng-aura';
import { PropertyDataCleaner } from '../../../utils/property-data.cleaner';

export interface PropertyAddressModel {
  street: string;
  city: string;
  zipCode: string;
  country: PropertyCountry;
  complement: string;
  district: string;
  houseNumber: string;
  label: string;
  region: string;
  cityCode: string;
}

export interface PropertyCoordinatesModel {
  latitude: number | null;
  longitude: number | null;
}

export interface PropertyLocationModel {
  address: PropertyAddressModel;
  coordinates: PropertyCoordinatesModel;
  geolocationType: PropertyGeolocationType | null;
}

@Component({
  selector: 'etxe-property-location-form',
  standalone: true,
  imports: [CommonModule, MatIconModule, OpenStreetMapComponent],
  templateUrl: './location-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyLocationFormComponent
  extends AbstractSignalFormComponent<PropertyLocationDto, PropertyLocationModel>
  implements OnInit
{
  radius = signal<number>(400);

  displayOptions = [
    {
      label: 'Quartier',
      value: PropertyGeolocationType.NEIGHBORHOOD,
      radius: 400,
      description: 'Affiche un rayon restreint (~400 m) autour du bien.',
    },
    {
      label: 'Ville',
      value: PropertyGeolocationType.CITY,
      radius: 2000,
      description: 'Affiche le périmètre élargi de la commune (~2 km).',
    },
    {
      label: 'Région',
      value: PropertyGeolocationType.REGION,
      radius: 10000,
      description: 'Affiche uniquement la zone régionale (~10 km).',
    },
  ];

  override model: WritableSignal<PropertyLocationModel> = signal<PropertyLocationModel>({
    address: {
      street: '',
      city: '',
      zipCode: '',
      country: PropertyCountry.FR,
      complement: '',
      district: '',
      houseNumber: '',
      label: '',
      region: '',
      cityCode: '',
    },
    coordinates: {
      latitude: null,
      longitude: null,
    },
    geolocationType: PropertyGeolocationType.NEIGHBORHOOD,
  });

  override formTree = form(this.model, (b) => {
    required(b.geolocationType);
  });

  ngOnInit() {
    const type = this.dto()?.geolocationType || PropertyGeolocationType.NEIGHBORHOOD;
    this.radius.set(this.getRadius(type));
  }

  setDisplayType(type: PropertyGeolocationType) {
    this.model.update((m) => ({ ...m, geolocationType: type }));
    this.radius.set(this.getRadius(type));
  }

  getRadius(type: PropertyGeolocationType | null | undefined): number {
    switch (type) {
      case PropertyGeolocationType.NEIGHBORHOOD:
        return 800;
      case PropertyGeolocationType.CITY:
        return 2000;
      case PropertyGeolocationType.REGION:
        return 10000;
      default:
        return 800;
    }
  }

  protected override buildModelFromDto(dto: PropertyLocationDto): PropertyLocationModel {
    const geoType = dto?.geolocationType ?? PropertyGeolocationType.NEIGHBORHOOD;
    this.radius.set(this.getRadius(geoType));

    return {
      address: {
        street: dto?.address?.street ?? '',
        city: dto?.address?.city ?? '',
        zipCode: dto?.address?.zipCode ?? '',
        country: dto?.address?.country ?? PropertyCountry.FR,
        complement: dto?.address?.complement ?? '',
        district: dto?.address?.district ?? '',
        houseNumber: dto?.address?.houseNumber ?? '',
        label: dto?.address?.label ?? '',
        region: dto?.address?.region ?? '',
        cityCode: dto?.address?.cityCode ?? '',
      },
      coordinates: {
        latitude: dto?.coordinates?.latitude ?? null,
        longitude: dto?.coordinates?.longitude ?? null,
      },
      geolocationType: geoType,
    };
  }

  protected override buildDtoFromModel(model: PropertyLocationModel): PropertyLocationDto {
    const rawDto: PropertyLocationDto = {
      ...(this.dto() || ({} as PropertyLocationDto)),
      address: {
        street: model.address.street,
        city: model.address.city,
        zipCode: model.address.zipCode,
        country: model.address.country,
        complement: model.address.complement || undefined,
        district: model.address.district || undefined,
        houseNumber: model.address.houseNumber || undefined,
        label: model.address.label || undefined,
        region: model.address.region || undefined,
        cityCode: model.address.cityCode || undefined,
      },
      coordinates: {
        latitude: model.coordinates.latitude ?? 0,
        longitude: model.coordinates.longitude ?? 0,
      },
      geolocationType: model.geolocationType ?? PropertyGeolocationType.NEIGHBORHOOD,
    };

    return PropertyDataCleaner.deepClean(rawDto) as PropertyLocationDto;
  }
}
